import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Feather, LogOut, Sparkles, BookOpen, Library, User, Heart } from 'lucide-react';
import PromptGenerator from '@/components/PromptGenerator';
import StoryEditor from '@/components/StoryEditor';
import StoryLibrary from '@/components/StoryLibrary';
import Favorites from '@/components/Favorites';
import PromptCollections from '@/components/PromptCollections';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [activeTab, setActiveTab] = useState('generate');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleUsePrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setActiveTab('write');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-full">
              <Feather className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="app-title">Flash Fiction</h1>
              <p className="text-xs text-gray-500">Creative Writing Studio</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-gray-500" data-testid="user-email">{user?.email}</p>
            </div>
            {user?.picture ? (
              <button
                onClick={() => navigate('/profile')}
                className="rounded-full ring-2 ring-transparent hover:ring-purple-400 transition-all"
                data-testid="profile-avatar-button"
              >
                <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full object-cover" />
              </button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/profile')}
                className="flex items-center space-x-2"
                data-testid="profile-button"
              >
                <User className="h-4 w-4" />
                <span>Profile</span>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex items-center space-x-2"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl" data-testid="dashboard-title">Welcome, {user?.name}!</CardTitle>
            <CardDescription>Generate prompts, write stories, and manage your collection</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="generate" className="flex items-center space-x-2" data-testid="tab-generate">
                  <Sparkles className="h-4 w-4" />
                  <span>Generate</span>
                </TabsTrigger>
                <TabsTrigger value="write" className="flex items-center space-x-2" data-testid="tab-write">
                  <BookOpen className="h-4 w-4" />
                  <span>Write</span>
                </TabsTrigger>
                <TabsTrigger value="favorites" className="flex items-center space-x-2" data-testid="tab-favorites">
                  <Heart className="h-4 w-4" />
                  <span>Favorites</span>
                </TabsTrigger>
                <TabsTrigger value="library" className="flex items-center space-x-2" data-testid="tab-library">
                  <Library className="h-4 w-4" />
                  <span>Library</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="generate" className="mt-6">
                <PromptGenerator onUsePrompt={handleUsePrompt} />
                <PromptCollections onUsePrompt={handleUsePrompt} />
              </TabsContent>

              <TabsContent value="write" className="mt-6">
                <StoryEditor initialPrompt={selectedPrompt} onClearPrompt={() => setSelectedPrompt('')} />
              </TabsContent>

              <TabsContent value="favorites" className="mt-6">
                <Favorites onUsePrompt={handleUsePrompt} />
              </TabsContent>

              <TabsContent value="library" className="mt-6">
                <StoryLibrary />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;
